import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import type { SwitchSize } from "./Switch.types";
import { duration, easing } from "../../../design-system/tokens/transitions";

/**
 * Per-size dimensions for track + thumb.
 * Values match the Figma `_switch-small` / `_switch-medium` specs.
 */
const SIZE_PX: Record<
  SwitchSize,
  { track: [number, number]; thumb: number; pad: number }
> = {
  xs: { track: [32, 20], thumb: 16, pad: 2 },
  sm: { track: [40, 24], thumb: 20, pad: 2 },
  md: { track: [48, 28], thumb: 24, pad: 2 },
};

export const Track = styled.button<{
  $size: SwitchSize;
  $on: boolean;
  $disabled: boolean;
}>`
  appearance: none;
  border: 1px solid ${rawColors.stroke[200]};
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: ${({ $size }) => SIZE_PX[$size].pad}px;
  width: ${({ $size }) => SIZE_PX[$size].track[0]}px;
  height: ${({ $size }) => SIZE_PX[$size].track[1]}px;
  border-radius: 999px;
  background-color: ${({ $on }) =>
    $on ? rawColors.primary[500] : rawColors.grey[300]};
  border-color: ${({ $on }) =>
    $on ? rawColors.primary[500] : rawColors.stroke[200]};
  transition: background-color ${duration.fast} ${easing.smooth}, border-color ${duration.fast} ${easing.smooth};
  box-sizing: border-box;

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}
`;

export const Thumb = styled.span<{ $size: SwitchSize; $on: boolean }>`
  display: inline-block;
  width: ${({ $size }) => SIZE_PX[$size].thumb}px;
  height: ${({ $size }) => SIZE_PX[$size].thumb}px;
  border-radius: 999px;
  /* Design: solid brand track with a white thumb in both states. */
  background-color: ${rawColors.structural.white};
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1), 0 1px 0.5px rgba(0, 0, 0, 0.06);
  transform: ${({ $size, $on }) => {
    if (!$on) return "translateX(0)";
    const { track, thumb, pad } = SIZE_PX[$size];
    const slide = track[0] - thumb - pad * 2;
    return `translateX(${slide}px)`;
  }};
  transition: transform ${duration.fast} ${easing.smooth}, background-color ${duration.fast} ${easing.smooth};
`;
